// 3rd Party
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Analytics from 'analytics-opencare';
import _ from 'lodash';

// Components
import {
  VisuallyHidden,
  ColoredButton,
  Div,
  Span,
} from 'common/styles/oc-styled-components';

// Other
import { selectDataUser } from 'connect/selectors';

import { BRAND_COLORS } from 'brand-guidelines/colors';
import { NUM_DAYS_IN_WEEK } from 'common/constants';
import { pluralize } from 'utils/string-utils';

import { useFunnelStorage } from '../../../funnel/storage/Storage';
import {
  getDateForScreenReader,
  getActiveSlots,
  getDatesToDisplay,
  getTimeFromStartTime,
  getNearestDateWithSlots,
  numDaysDisplayed,
} from '../../../common/helpers/appointment-slot-helpers';
import { selectDataClinic } from '../../connect/selectors';
import { SlotPickerTimeSlots } from '../../../common/components/SlotPicker/SlotPickerTimeSlots/SlotPickerTimeSlots';

import {
  DatesList,
  Month,
  NavigationButton,
  Weekday,
  Slot,
  SlotsContent,
  MonthRow,
  ChevronLeft,
  ChevronRight,
} from './styled-components';

const analytics = new Analytics();

const NUM_WEEKS_DISPLAYED = 8;
const REQUEST_SOONER_THRESHOLD = 12;
const NUM_DAYS_DISPLAYED = NUM_WEEKS_DISPLAYED * NUM_DAYS_IN_WEEK;

// Component
const InstantBookView = ({ allSlots, onRequestAppointment }) => {
  const funnelStorage = useFunnelStorage();
  const [activeSlots, setActiveSlots] = useState({});
  const [month, setMonth] = useState();
  const [startDate, setStartDate] = useState();
  // const [selectedDate, setSelectedDate] = useState();
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const clinic = useSelector(selectDataClinic);
  const user = useSelector(selectDataUser);

  // find the earliest start date from all available slots of practice
  useEffect(() => {
    const date = getNearestDateWithSlots(allSlots);
    const formattedDate = moment(date).startOf('day');
    setStartDate(formattedDate);
  }, [allSlots]);

  useEffect(() => {
    if (startDate) {
      const slots = getActiveSlots(startDate, allSlots);
      setActiveSlots(slots);
      setMonth(startDate.format('MMMM'));
    }
  }, [startDate]);

  const displayedDays = getDatesToDisplay('ddd[,]D', startDate) || [];
  const slotDays = getDatesToDisplay('YYYY-MM-DD', startDate) || [];

  useEffect(() => {
    setSelectedSlots([]);
    for (let i = 0; i < slotDays.length; i += 1) {
      const slots = activeSlots[slotDays[i]] || [];
      if (slots.length > 0) {
        setSelectedSlots(slots);
        setSelectedDateIndex(i);
        break;
      }
    }
  }, [startDate, activeSlots]);

  const handleShowPrevThreeDays = () => {
    const newDate = startDate
      .clone()
      .startOf('day')
      .subtract(numDaysDisplayed, 'day');
    setStartDate(newDate);
  };

  const handleShowNextThreeDays = () => {
    const newDate = startDate
      .clone()
      .startOf('day')
      .add(numDaysDisplayed, 'day');
    setStartDate(newDate);
  };

  const handleDateSelected = (dateIndex, selectedSlot) => {
    setSelectedDateIndex(dateIndex);
    setSelectedSlots(selectedSlot);
    setMonth(moment(selectedSlot[0].date).format('MMMM'));
  };

  const handleSlotSelected = (slot, slotIndex, dayIndex) => {
    onRequestAppointment(slot.id);
    const slotPosition = slotIndex * 3 + dayIndex + 1;
    analytics.trackClick({
      pageType: analytics.pageTypes.PRACTICE_PROFILE,
      pageName: clinic.name,
      name: 'instant-book',
      section: clinic.name,
      position: [slotPosition],
      userId: _.get(user, 'id'),
      practiceId: slot.provider?.clinic.id,
      funnelConfigId: _.get(funnelStorage, 'config.id'),
      content: {
        date: slot.date,
        time: getTimeFromStartTime(slot.time, 'HHmm'),
      },
    });
  };

  const handleRequestTime = () => {
    onRequestAppointment();
    analytics.trackClick({
      pageType: analytics.pageTypes.PRACTICE_PROFILE,
      pageName: clinic.name,
      name: 'instant-book',
      section: clinic.name,
      userId: _.get(user, 'id'),
      practiceId: clinic.id,
      funnelConfigId: _.get(funnelStorage, 'config.id'),
    });
  };

  const today = moment();
  const firstSlotDay = moment(slotDays[0]);
  const lastSlotDay = moment(slotDays.at(-1));

  const isPrevButtonDisabled = firstSlotDay.diff(today, 'days') <= 0;
  const isNextButtonDisabled =
    lastSlotDay.diff(today, 'days') >= NUM_DAYS_DISPLAYED;

  return (
    <SlotsContent aria-label="Choose an appointment timeslot">
      <MonthRow>
        <NavigationButton
          onClick={handleShowPrevThreeDays}
          aria-label={`Previous ${numDaysDisplayed} ${pluralize(
            numDaysDisplayed,
            'day',
            'days',
          )}`}
          disabled={isPrevButtonDisabled}
        >
          <ChevronLeft aria-hidden disabled={isPrevButtonDisabled} />
        </NavigationButton>

        <Month aria-hidden>{month}</Month>

        <NavigationButton
          onClick={handleShowNextThreeDays}
          aria-label={`Next ${numDaysDisplayed} ${pluralize(
            numDaysDisplayed,
            'day',
            'days',
          )}`}
          disabled={isNextButtonDisabled}
        >
          <ChevronRight aria-hidden disabled={isNextButtonDisabled} />
        </NavigationButton>
      </MonthRow>
      <SlotsContent>
        <DatesList aria-label="Appointment dates">
          <Weekday disabled>Su</Weekday>
          <Weekday disabled>Mo</Weekday>
          <Weekday disabled>Tu</Weekday>
          <Weekday disabled>We</Weekday>
          <Weekday disabled>Th</Weekday>
          <Weekday disabled>Fr</Weekday>
          <Weekday disabled>Sa</Weekday>
          {startDate
            && displayedDays.map((day, i) => {
              const slotDay = slotDays[i];
              const slots = activeSlots[slotDay] || [];
              const dayDisplay = day.split(',');
              const isToday = moment(slotDay).isSame(moment(), 'day');

              return (
                <Slot
                  key={slotDay}
                  $isSelected={selectedDateIndex === i && Boolean(slots.length)}
                  disabled={!slots.length}
                  $isToday={isToday}
                  onClick={() => handleDateSelected(i, slots)}
                >
                  <VisuallyHidden>
                    {getDateForScreenReader(slotDay)}
                  </VisuallyHidden>
                  <p aria-hidden>{dayDisplay[1]}</p>
                </Slot>
              );
            })}
        </DatesList>

        <SlotPickerTimeSlots
          dayIndex={selectedDateIndex}
          handleSelect={handleSlotSelected}
          slots={selectedSlots}
        />

        {allSlots.length < REQUEST_SOONER_THRESHOLD
          && (
            <Div textAlign="center">
              <ColoredButton
                id="instant-book-request-btn"
                size="medium"
                shape="round"
                background={BRAND_COLORS.white}
                color={BRAND_COLORS.darkGreen}
                onClick={handleRequestTime}
                data-testid="instant-book-request-btn"
              >
                <Span>Request a sooner time</Span>
              </ColoredButton>
            </Div>
          )
        }
      </SlotsContent>
    </SlotsContent>
  );
};

InstantBookView.propTypes = {
  allSlots: PropTypes.array,
  onRequestAppointment: PropTypes.func,
};

export default InstantBookView;
